import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh, SortFilterTh } from "../components/tableFilters";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import * as XLSX from "xlsx";
import { Order, OrderStatus, Firm, Workshop, OrderUnit, Technic } from "../types";
import {
  orderService,
  firmService,
  workshopService,
} from "../services/dataService";
import { useAuth } from "../context/AuthContext";
import { useExchangeRates } from "../context/ExchangeRateContext";
import ImageManager from "../components/ImageManager";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import OrderCostsModal from "../components/OrderCostsModal";
import NoteModal from "../components/NoteModal";
import {
  formatCurrency,
  formatNumber,
  turkishIncludes,
} from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import OrderDetailModal from "../components/OrderDetailModal";
import OrderEditModal from "../components/OrderEditModal";
import TechnicModal from "../components/TechnicModal";
import {
  TechnicMatchMode,
  TECHNIC_MATCH_MODES,
  getTechnicMatchHint,
  matchesTechnicFilter,
} from "../utils/technicMatch";
import "./Orders.css";

const STORAGE_KEY = "orders-filters";

// localStorage'dan filtreleri yükle
const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        statusFilter: parsed.statusFilter || [],
        firmFilter: parsed.firmFilter || [],
        workshopFilter: parsed.workshopFilter || [],
        dateFilter: parsed.dateFilter || { startDate: "", endDate: "" },
        searchTerm: parsed.searchTerm || "",
        activeStatusFilter: parsed.activeStatusFilter || "active",
        selectedTechnics: parsed.selectedTechnics || [],
        technicMatchMode: parsed.technicMatchMode || "exact",
      };
    }
  } catch (error) {
    console.error("Failed to load filters from storage:", error);
  }
  return {
    statusFilter: [],
    firmFilter: [],
    workshopFilter: [],
    dateFilter: { startDate: "", endDate: "" },
    searchTerm: "",
    activeStatusFilter: "active",
    selectedTechnics: [],
    technicMatchMode: "exact",
  };
};

const Orders: React.FC = () => {
  const savedFilters = loadFiltersFromStorage();

  const [orders, setOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]); // Filtrelenmemiş tüm siparişler
  const [loading, setLoading] = useState(true);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>(
    savedFilters.statusFilter,
  );
  const [firmFilter, setFirmFilter] = useState<string[]>(
    savedFilters.firmFilter,
  );
  const [workshopFilter, setWorkshopFilter] = useState<string[]>(
    savedFilters.workshopFilter,
  );
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>(
    savedFilters.activeStatusFilter,
  );
  const [firmSearch, setFirmSearch] = useState<string>("");
  const [workshopSearch, setWorkshopSearch] = useState<string>("");
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [workshopDropdownOpen, setWorkshopDropdownOpen] = useState(false);
  const [activeStatusDropdownOpen, setActiveStatusDropdownOpen] =
    useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: savedFilters.dateFilter.startDate || "",
    endDate: savedFilters.dateFilter.endDate || "",
  });
  const [summaryDateFilter, setSummaryDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  // Teknik filtresi
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>(
    savedFilters.selectedTechnics,
  );
  const [technicMatchMode, setTechnicMatchMode] = useState<TechnicMatchMode>(
    savedFilters.technicMatchMode,
  );
  const [showTechnicModal, setShowTechnicModal] = useState(false);
  const [statusSummary, setStatusSummary] = useState<
    {
      Status: string;
      Count: number;
      TotalValue: number;
      TotalQuantity: number;
    }[]
  >([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  // QR Print Modal state
  const [qrPrintModal, setQrPrintModal] = useState<{
    show: boolean;
    order: Order | null;
    qrCodeDataUrl: string | null;
  }>({ show: false, order: null, qrCodeDataUrl: null });

  // Order Costs Modal state
  const [orderCostsModal, setOrderCostsModal] = useState<{
    isOpen: boolean;
    orderId: string;
    modelId: string;
  }>({ isOpen: false, orderId: "", modelId: "" });

  // Arama ve sıralama state'leri
  const [searchTerm, setSearchTerm] = useState<string>(savedFilters.searchTerm);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const [expandedTechnics, setExpandedTechnics] = useState<Set<string>>(
    new Set(),
  );

  // Note Modal state
  const [noteModal, setNoteModal] = useState<{
    isOpen: boolean;
    note: string;
    orderCode: string;
    modelName: string;
  }>({ isOpen: false, note: "", orderCode: "", modelName: "" });
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });

  const navigate = useNavigate();
  const { user } = useAuth();
  const { usdRate, eurRate } = useExchangeRates();

  // Numune durumunu toggle et
  const handleToggleSample = async (order: Order) => {
    try {
      const newSampleStatus = !order.isSample;

      await orderService.updateSampleStatus(order.orderId, newSampleStatus);

      // Local state'i güncelle
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === order.orderId ? { ...o, isSample: newSampleStatus } : o,
        ),
      );

      setAllOrders((prev) =>
        prev.map((o) =>
          o.orderId === order.orderId ? { ...o, isSample: newSampleStatus } : o,
        ),
      );
    } catch (error) {
      console.error("Numune durumu güncellenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Numune durumu güncellenemedi!",
        type: "error",
      });
    }
  };

  // Sipariş silme fonksiyonu
  const handleDeleteOrder = async (order: Order) => {
    setConfirmModal({
      isOpen: true,
      message: `"${order.firm?.firmName || "Firma"} - ${
        order.model?.modelName || "Model"
      }" siparişini silmek istediğinizden emin misiniz?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          await orderService.delete(order.orderId);

          // Local state'leri güncelle
          setOrders((prev) => prev.filter((o) => o.orderId !== order.orderId));
          setAllOrders((prev) =>
            prev.filter((o) => o.orderId !== order.orderId),
          );

          setAlertModal({
            isOpen: true,
            message: "Sipariş başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("Sipariş silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Sipariş silinemedi!",
            type: "error",
          });
        }
      },
    });
  };

  // QR Modal'ı aç
  const handleShowQrModal = async (order: Order) => {
    let qrDataUrl: string | null = null;
    try {
      if (order.qrCodeUrl || order.qrCode) {
        qrDataUrl = await QRCode.toDataURL(
          order.qrCodeUrl || order.qrCode || "",
          {
            width: 300,
            margin: 2,
          },
        );
      }
    } catch (error) {
      console.error("QR kod oluşturulamadı:", error);
    }

    setQrPrintModal({ show: true, order, qrCodeDataUrl: qrDataUrl });
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    firmFilter,
    workshopFilter,
    dateFilter,
    activeStatusFilter,
  ]);

  // Filtreleri localStorage'a kaydet
  useEffect(() => {
    const filters = {
      statusFilter,
      firmFilter,
      workshopFilter,
      dateFilter,
      searchTerm,
      activeStatusFilter,
      selectedTechnics,
      technicMatchMode,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    statusFilter,
    firmFilter,
    workshopFilter,
    dateFilter,
    searchTerm,
    activeStatusFilter,
    selectedTechnics,
    technicMatchMode,
  ]);

  useEffect(() => {
    loadFirms();
    loadWorkshops();
  }, []);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".searchable-select") &&
        !target.closest(".action-dropdown-container") &&
        !target.closest(".action-dropdown-menu")
      ) {
        setFirmDropdownOpen(false);
        setStatusDropdownOpen(false);
        setWorkshopDropdownOpen(false);
        setActiveStatusDropdownOpen(false);
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Scroll olduğunda dropdown'u kapat
  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openDropdownId]);

  // Exchange rates, orders veya summaryDateFilter değiştiğinde summary'i yeniden hesapla
  useEffect(() => {
    if (orders.length > 0 || (orders.length === 0 && allOrders.length > 0)) {
      setStatusSummary(calculateStatusSummary(orders));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usdRate, eurRate, orders, summaryDateFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      // Aktif/Pasif filtresine göre doğru endpoint'i çağır
      let data: Order[];
      if (activeStatusFilter === "active") {
        data = await orderService.getAll(); // Sadece aktif siparişler
      } else if (activeStatusFilter === "inactive") {
        data = await orderService.getPassiveOrders(); // Sadece pasif siparişler
      } else {
        data = await orderService.getAllOrders(); // Tüm siparişler
      }

      // API'dan zaten createdAt'a göre sıralı geliyor
      setAllOrders(data); // Tüm siparişleri kaydet

      let filteredData = data;

      // Manager olmayan kullanıcılar sadece kendi atölyelerindeki işleri görebilir
      if (
        user &&
        user.role !== "Manager" &&
        user.workshops &&
        user.workshops.length > 0
      ) {
        const userWorkshopIds = user.workshops.map((w) => w.workshopId);
        filteredData = filteredData.filter(
          (order) =>
            order.workshopId && userWorkshopIds.includes(order.workshopId),
        );
      }

      if (statusFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) =>
            order.status !== undefined && statusFilter.includes(order.status),
        );
      }
      if (firmFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) => order.firmId && firmFilter.includes(order.firmId),
        );
      }
      if (workshopFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) =>
            order.workshopId && workshopFilter.includes(order.workshopId),
        );
      }

      // Tarih filtresi - sadece "Tamamlandı" status'unda çalışır
      if (
        statusFilter.length === 1 &&
        statusFilter.includes(OrderStatus.Tamamlandi) &&
        (dateFilter.startDate || dateFilter.endDate)
      ) {
        filteredData = filteredData.filter((order) => {
          if (!order.completionDate) return false;

          const orderDate = new Date(order.completionDate);
          const startDate = dateFilter.startDate
            ? new Date(dateFilter.startDate)
            : null;
          const endDate = dateFilter.endDate
            ? new Date(dateFilter.endDate)
            : null;

          if (startDate && orderDate < startDate) return false;
          if (endDate && orderDate > endDate) return false;

          return true;
        });
      }

      setOrders(filteredData);
      setStatusSummary(calculateStatusSummary(filteredData)); // Filtrelenmiş siparişleri kullan
    } catch (error: any) {
      console.error("❌ Failed to load orders:", error);
      setAlertModal({
        isOpen: true,
        message: "Siparişler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStatusSummary = (ordersList: Order[]) => {
    let filteredOrders = ordersList;

    // Durum özeti için tarih filtresi uygula
    if (summaryDateFilter.startDate || summaryDateFilter.endDate) {
      filteredOrders = ordersList.filter((order) => {
        if (!order.completionDate) return false;

        const orderDate = new Date(order.completionDate);
        const startDate = summaryDateFilter.startDate
          ? new Date(summaryDateFilter.startDate)
          : null;
        const endDate = summaryDateFilter.endDate
          ? new Date(summaryDateFilter.endDate)
          : null;

        if (startDate && orderDate < startDate) return false;
        if (endDate && orderDate > endDate) return false;

        return true;
      });
    }

    const summary: {
      [key: string]: {
        Count: number;
        TotalValue: number;
        TotalQuantity: number;
      };
    } = {};

    filteredOrders.forEach((order) => {
      const statusKey = getStatusText(order.status);
      if (!summary[statusKey]) {
        summary[statusKey] = { Count: 0, TotalValue: 0, TotalQuantity: 0 };
      }
      summary[statusKey].Count += 1;
      summary[statusKey].TotalQuantity += order.quantity || 0;

      // Tüm siparişler için fiyat hesapla (fiyat varsa)
      if (order.price && order.quantity) {
        const totalPrice = order.price * order.quantity;

        // Döviz cinsine göre TL'ye çevir
        let priceInTRY = totalPrice;
        const currency = order.priceCurrency || order.currency || "TRY";

        if (currency === "USD" && usdRate) {
          priceInTRY = totalPrice * usdRate;
        } else if (currency === "EUR" && eurRate) {
          priceInTRY = totalPrice * eurRate;
        }

        summary[statusKey].TotalValue += priceInTRY;
      }
    });

    return Object.entries(summary).map(([Status, data]) => ({
      Status,
      Count: data.Count,
      TotalValue: data.TotalValue,
      TotalQuantity: data.TotalQuantity,
    }));
  };

  const loadFirms = async () => {
    try {
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Failed to load firms:", error);
    }
  };

  const loadWorkshops = async () => {
    try {
      const data = await workshopService.getAll();
      setWorkshops(data);
    } catch (error) {
      console.error("❌ Failed to load workshops:", error);
    }
  };

  // Durum kartına tıklandığında filtreleme yap
  const handleStatusCardClick = (statusText: string) => {
    // Durum metnine göre OrderStatus değerini bul
    let newStatus: OrderStatus | null = null;

    switch (statusText) {
      case "Atanmadı":
        newStatus = OrderStatus.Atanmadi;
        break;
      case "İşlemde":
        newStatus = OrderStatus.Islemde;
        break;
      case "Tamamlandı":
        newStatus = OrderStatus.Tamamlandi;
        break;
      case "İptal Edildi":
        newStatus = OrderStatus.IptalEdildi;
        break;
    }

    if (newStatus !== null) {
      setStatusFilter([newStatus]);

      // Tamamlandı dışındaki durumlar için tarih filtrelerini sıfırla
      if (newStatus !== OrderStatus.Tamamlandi) {
        setDateFilter({ startDate: "", endDate: "" });
      }
    }
  };

  const getStatusText = (status?: OrderStatus): string => {
    if (status === undefined || status === null) return "Atanmadı";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "Atanmadı";
      case OrderStatus.Islemde:
        return "İşlemde";
      case OrderStatus.Tamamlandi:
        return "Tamamlandı";
      case OrderStatus.IptalEdildi:
        return "İptal Edildi";
      default:
        return "Atanmadı";
    }
  };

  const getStatusClass = (status?: OrderStatus): string => {
    if (status === undefined) return "status-atanmadi";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "status-atanmadi";
      case OrderStatus.Islemde:
        return "status-islemde";
      case OrderStatus.Tamamlandi:
        return "status-tamamlandi";
      case OrderStatus.IptalEdildi:
        return "status-iptal";
      default:
        return "status-atanmadi";
    }
  };

  const getUnitText = (unit?: OrderUnit): string => {
    const unitMap: { [key: number]: string } = {
      [OrderUnit.Adet]: "Adet",
      [OrderUnit.Metre]: "Metre",
      [OrderUnit.Takim]: "Takım",
      [OrderUnit.Pano]: "Pano",
    };
    return unitMap[unit ?? OrderUnit.Adet] || "Adet";
  };

  // Sıralama fonksiyonu
  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Verileri sıralayan fonksiyon
  const getSortedOrders = (ordersToSort: Order[]) => {
    // Eğer manuel sıralama yoksa, API'dan gelen sırayı koru
    if (!sortConfig.key) {
      return ordersToSort;
    }

    const sortedOrders = [...ordersToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case "firm":
          aValue = a.firm?.firmName || "";
          bValue = b.firm?.firmName || "";
          break;
        case "model":
          aValue = a.model?.modelName || "";
          bValue = b.model?.modelName || "";
          break;
        case "quantity":
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case "price":
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case "workshop":
          aValue = a.workshop?.name || "";
          bValue = b.workshop?.name || "";
          break;
        case "operator":
          aValue = a.operator
            ? `${a.operator.firstName} ${a.operator.lastName}`
            : "";
          bValue = b.operator
            ? `${b.operator.firstName} ${b.operator.lastName}`
            : "";
          break;
        case "modelist":
          aValue = a.modelistUser
            ? `${a.modelistUser.firstName} ${a.modelistUser.lastName}`
            : "";
          bValue = b.modelistUser
            ? `${b.modelistUser.firstName} ${b.modelistUser.lastName}`
            : "";
          break;
        case "status":
          aValue = a.status || 0;
          bValue = b.status || 0;
          break;
        case "acceptanceDate":
          aValue = a.acceptanceDate ? new Date(a.acceptanceDate).getTime() : 0;
          bValue = b.acceptanceDate ? new Date(b.acceptanceDate).getTime() : 0;
          break;
        case "deadline":
          aValue = a.deadline ? new Date(a.deadline).getTime() : 0;
          bValue = b.deadline ? new Date(b.deadline).getTime() : 0;
          break;
        case "completionDate":
          aValue = a.completionDate ? new Date(a.completionDate).getTime() : 0;
          bValue = b.completionDate ? new Date(b.completionDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sortedOrders;
  };

  // Teknik seçimi (aç/kapa)
  const handleTechnicSelect = (technic: Technic) => {
    setSelectedTechnics((prev) =>
      prev.some((t) => t.technicId === technic.technicId)
        ? prev.filter((t) => t.technicId !== technic.technicId)
        : [...prev, technic],
    );
  };

  const handleClearTechnics = () => setSelectedTechnics([]);

  // Teknik filtresi - eşleşme moduna göre
  const getTechnicFilteredOrders = (ordersToFilter: Order[]) => {
    if (selectedTechnics.length === 0) return ordersToFilter;

    const selectedTechnicNames = selectedTechnics.map((t) => t.name);
    return ordersToFilter.filter((order) => {
      const orderTechnicNames = (order.orderTechnics || [])
        .map((ot) => ot.technic?.name || "")
        .filter(Boolean);
      return matchesTechnicFilter(
        orderTechnicNames,
        selectedTechnicNames,
        technicMatchMode,
      );
    });
  };

  // Arama fonksiyonu
  const getFilteredOrders = (ordersToFilter: Order[]) => {
    if (!searchTerm.trim()) return ordersToFilter;

    return ordersToFilter.filter((order) => {
      return (
        turkishIncludes(order.orderId || "", searchTerm) ||
        turkishIncludes(order.firm?.firmName || "", searchTerm) ||
        turkishIncludes(order.model?.modelName || "", searchTerm) ||
        turkishIncludes(order.model?.modelCode || "", searchTerm) ||
        turkishIncludes(order.workshop?.name || "", searchTerm) ||
        turkishIncludes(order.operator?.firstName || "", searchTerm) ||
        turkishIncludes(order.operator?.lastName || "", searchTerm) ||
        turkishIncludes(order.modelistUser?.firstName || "", searchTerm) ||
        turkishIncludes(order.modelistUser?.lastName || "", searchTerm) ||
        turkishIncludes(getStatusText(order.status), searchTerm) ||
        order.quantity?.toString().includes(searchTerm.trim()) ||
        order.price?.toString().includes(searchTerm.trim())
      );
    });
  };

  // Hem aramayı hem sıralamayı uygulayan fonksiyon
  const getProcessedOrders = () => {
    const filtered = getFilteredOrders(getTechnicFilteredOrders(orders));
    return getSortedOrders(filtered);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Excel'e aktar fonksiyonu
  const exportToExcel = () => {
    const dataToExport = getProcessedOrders().map((order) => {
      const rowData: any = {
        "Order ID": order.orderId,
        Firma: order.firm?.firmName || "-",
        Model: order.model?.modelName || "-",
        Miktar: order.quantity || 0,
        Birim: getUnitText(order.unit),
        "Parça/Takım":
          order.unit === OrderUnit.Takim && order.pieceCount
            ? `${order.pieceCount} parça`
            : "-",
        Numune: order.isSample ? "Evet" : "Hayır",
        Teknikler:
          order.orderTechnics
            ?.map((ot) => ot.technic?.name || "-")
            .join(", ") || "-",
      };

      // Manager için fiyat bilgilerini ekle
      if (user?.role === "Manager") {
        rowData["Birim Fiyat"] = order.price ? formatNumber(order.price) : "-";
        rowData["Döviz"] = order.priceCurrency || order.currency || "TRY";
      }

      rowData["Atölye"] = order.workshop?.name || "-";
      rowData["Operatör"] = order.operator
        ? `${order.operator.firstName} ${order.operator.lastName}`
        : "-";
      rowData["Modelist"] = order.modelistUser
        ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}`
        : "-";
      rowData["Durum"] = getStatusText(order.status);
      rowData["Kabul Tarihi"] = formatDate(order.acceptanceDate);
      rowData["Termin"] = formatDate(order.deadline);
      rowData["Bitiş Tarihi"] = formatDate(order.completionDate);

      return rowData;
    });

    // Worksheet oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Siparişler");

    // Excel dosyasını indir
    const fileName = `Siparisler_${new Date()
      .toLocaleDateString("tr-TR")
      .replace(/\./g, "-")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const processedOrders = getProcessedOrders();
  const orderColDefs = useMemo(
    () => [
      { key: "orderId", value: (o: Order) => o.orderId || "" },
      { key: "firm", value: (o: Order) => o.firm?.firmName || "" },
      { key: "model", value: (o: Order) => o.model?.modelName || "" },
      { key: "quantity", type: "float", value: (o: Order) => o.quantity ?? null },
      { key: "unit", value: (o: Order) => getUnitText(o.unit) },
      { key: "pieceCount", type: "int", value: (o: Order) => o.pieceCount ?? null },
      { key: "technics", value: (o: Order) => (o.orderTechnics || []).map((ot) => ot.technic?.name || "").filter(Boolean).join(", ") },
      { key: "price", type: "decimal", value: (o: Order) => o.price ?? null },
      { key: "currency", value: (o: Order) => o.priceCurrency || o.currency || "TRY" },
      { key: "workshop", value: (o: Order) => o.workshop?.name || "" },
      { key: "modelist", value: (o: Order) => (o.modelistUser ? `${o.modelistUser.firstName} ${o.modelistUser.lastName}`.trim() : "") },
      { key: "status", value: (o: Order) => getStatusText(o.status) },
      { key: "acceptanceDate", type: "datetime", value: (o: Order) => o.acceptanceDate || null },
      { key: "deadline", type: "datetime", value: (o: Order) => o.deadline || null },
      { key: "completionDate", type: "datetime", value: (o: Order) => o.completionDate || null },
      { key: "note", value: (o: Order) => o.note || "" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: orderCf, filtered: displayOrders } = useTableColumnFilters(processedOrders, orderColDefs);

  if (loading) {
    return <PageLoader message="Siparişler yükleniyor..." />;
  }

  return (
    <div className="orders-container">
      {/* Sayfa Başlığı */}

      {/* Durum Özeti - Sadece Manager için */}
      {user?.role === "Manager" && (
        <div className="status-summary">
          <h3>Durum Özeti</h3>

          {/* Durum özeti tarih filtresi */}

          <div className="summary-cards">
            {statusSummary.map((item) => (
              <div
                key={item.Status}
                className="summary-card"
                onClick={() => handleStatusCardClick(item.Status)}
                style={{ cursor: "pointer" }}
              >
                <div className="summary-status">{item.Status}</div>
                <div className="summary-count">{item.Count} Sipariş</div>
                <div className="summary-quantity">
                  {item.TotalQuantity} Adet
                </div>
                <div className="summary-value">
                  Toplam: {formatCurrency(item.TotalValue)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="filters orders-filters">
        <div className="orders-filters-row">
        <div className="filter-group">
          <label>Arama:</label>
          <input
            type="text"
            placeholder="Order ID, Firma, model, atölye, operatör, desinatör ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Sipariş Durumu:</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() =>
                setActiveStatusDropdownOpen(!activeStatusDropdownOpen)
              }
            >
              <span>
                {activeStatusFilter === "active"
                  ? "Aktif Siparişler"
                  : activeStatusFilter === "inactive"
                    ? "Pasif Siparişler"
                    : "Tüm Siparişler"}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {activeStatusDropdownOpen && (
              <div className="select-dropdown">
                <div className="select-options">
                  <div
                    className="select-option"
                    onClick={() => {
                      setActiveStatusFilter("active");
                      setActiveStatusDropdownOpen(false);
                    }}
                  >
                    Aktif Siparişler
                  </div>
                  <div
                    className="select-option"
                    onClick={() => {
                      setActiveStatusFilter("inactive");
                      setActiveStatusDropdownOpen(false);
                    }}
                  >
                    Pasif Siparişler
                  </div>
                  <div
                    className="select-option"
                    onClick={() => {
                      setActiveStatusFilter("all");
                      setActiveStatusDropdownOpen(false);
                    }}
                  >
                    Tüm Siparişler
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>Durum:</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            >
              <span>
                {statusFilter.length === 0
                  ? "Tüm Durumlar"
                  : statusFilter.length === 1
                    ? getStatusText(statusFilter[0])
                    : `${statusFilter.length} durum seçili`}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {statusDropdownOpen && (
              <div className="select-dropdown">
                <div className="select-options">
                  <div
                    className="select-option-checkbox"
                    onClick={() => setStatusFilter([])}
                  >
                    <input
                      type="checkbox"
                      checked={statusFilter.length === 0}
                      readOnly
                    />
                    <span>Tüm Durumlar</span>
                  </div>
                  {[
                    OrderStatus.Atanmadi,
                    OrderStatus.Islemde,
                    OrderStatus.Tamamlandi,
                    OrderStatus.IptalEdildi,
                  ].map((status) => (
                    <div
                      key={status}
                      className="select-option-checkbox"
                      onClick={() => {
                        if (statusFilter.includes(status)) {
                          setStatusFilter(
                            statusFilter.filter((s) => s !== status),
                          );
                        } else {
                          setStatusFilter([...statusFilter, status]);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={statusFilter.includes(status)}
                        readOnly
                      />
                      <span>{getStatusText(status)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>Firma:</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setFirmDropdownOpen(!firmDropdownOpen)}
            >
              <span>
                {firmFilter.length === 0
                  ? "Tüm Firmalar"
                  : firmFilter.length === 1
                    ? firms.find((f) => f.firmId === firmFilter[0])?.firmName ||
                      "Seçiniz"
                    : `${firmFilter.length} firma seçili`}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {firmDropdownOpen && (
              <div className="select-dropdown">
                <input
                  type="text"
                  placeholder="Firma ara..."
                  value={firmSearch}
                  onChange={(e) => setFirmSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="select-options">
                  <div
                    className="select-option-checkbox"
                    onClick={() => setFirmFilter([])}
                  >
                    <input
                      type="checkbox"
                      checked={firmFilter.length === 0}
                      readOnly
                    />
                    <span>Tüm Firmalar</span>
                  </div>
                  {firms
                    .filter(
                      (f) =>
                        turkishIncludes(f.firmCode, firmSearch) ||
                        turkishIncludes(f.firmName, firmSearch),
                    )
                    .sort((a, b) => a.firmName.localeCompare(b.firmName))
                    .map((firm) => (
                      <div
                        key={firm.firmId}
                        className="select-option-checkbox"
                        onClick={() => {
                          if (firmFilter.includes(firm.firmId)) {
                            setFirmFilter(
                              firmFilter.filter((id) => id !== firm.firmId),
                            );
                          } else {
                            setFirmFilter([...firmFilter, firm.firmId]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={firmFilter.includes(firm.firmId)}
                          readOnly
                        />
                        <span>{firm.firmName}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>Atölye:</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setWorkshopDropdownOpen(!workshopDropdownOpen)}
            >
              <span>
                {workshopFilter.length === 0
                  ? "Tüm Atölyeler"
                  : workshopFilter.length === 1
                    ? workshops.find((w) => w.workshopId === workshopFilter[0])
                        ?.name || "Seçiniz"
                    : `${workshopFilter.length} atölye seçili`}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {workshopDropdownOpen && (
              <div className="select-dropdown">
                <input
                  type="text"
                  placeholder="Atölye ara..."
                  value={workshopSearch}
                  onChange={(e) => setWorkshopSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="select-options">
                  <div
                    className="select-option-checkbox"
                    onClick={() => setWorkshopFilter([])}
                  >
                    <input
                      type="checkbox"
                      checked={workshopFilter.length === 0}
                      readOnly
                    />
                    <span>Tüm Atölyeler</span>
                  </div>
                  {(user?.role === "Manager"
                    ? workshops
                    : workshops.filter((w) =>
                        user?.workshops?.some(
                          (uw) => uw.workshopId === w.workshopId,
                        ),
                      )
                  )
                    .filter((w) => turkishIncludes(w.name, workshopSearch))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((workshop) => (
                      <div
                        key={workshop.workshopId}
                        className="select-option-checkbox"
                        onClick={() => {
                          if (workshopFilter.includes(workshop.workshopId)) {
                            setWorkshopFilter(
                              workshopFilter.filter(
                                (id) => id !== workshop.workshopId,
                              ),
                            );
                          } else {
                            setWorkshopFilter([
                              ...workshopFilter,
                              workshop.workshopId,
                            ]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={workshopFilter.includes(workshop.workshopId)}
                          readOnly
                        />
                        <span>{workshop.name}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tarih filtreleri - sadece "Tamamlandı" statusunda görünür */}
        {statusFilter.length === 1 &&
          statusFilter.includes(OrderStatus.Tamamlandi) && (
            <>
              <div className="filter-group">
                <label>Başlangıç Tarihi:</label>
                <input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                />
              </div>
              <div className="filter-group">
                <label>Bitiş Tarihi:</label>
                <input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                />
              </div>
            </>
          )}

        {/* Teknik filtresi - detayları alt satırdaki technic-filter-bar'da */}
        <div className="filter-group technic-filter-group">
          <label>Teknikler:</label>
          <button
            type="button"
            className={`technic-filter-trigger ${
              selectedTechnics.length > 0 ? "is-active" : ""
            }`}
            onClick={() => setShowTechnicModal(true)}
          >
            <span className="technic-filter-trigger-icon">
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </span>
            <span className="technic-filter-trigger-text">
              {selectedTechnics.length === 0
                ? "Teknik Seç"
                : `${selectedTechnics.length} teknik seçili`}
            </span>
            <svg
              className="technic-filter-caret"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
        </div>

        {/* Filtreleri Temizle Butonu */}
        {(statusFilter.length > 0 ||
          firmFilter.length > 0 ||
          workshopFilter.length > 0 ||
          dateFilter.startDate ||
          dateFilter.endDate ||
          selectedTechnics.length > 0 ||
          activeStatusFilter !== "active") && (
          <div className="filter-group">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStatusFilter([]);
                setFirmFilter([]);
                setWorkshopFilter([]);
                setDateFilter({ startDate: "", endDate: "" });
                setSearchTerm("");
                setActiveStatusFilter("active");
                setSelectedTechnics([]);
                setTechnicMatchMode("exact");
                localStorage.removeItem(STORAGE_KEY);
              }}
              title="Tüm filtreleri temizle"
            >
              ✕ Filtreleri Temizle
            </button>
          </div>
        )}

        {/* Excel'e Aktar Butonu */}
        <div className="filter-group">
          <button
            className="btn btn-success"
            onClick={exportToExcel}
            disabled={getProcessedOrders().length === 0}
            title="Mevcut listeyi Excel'e aktar"
          >
            📊 Excel'e Aktar
          </button>
        </div>
        </div>

        {/* Teknik filtresi detay barı - sadece teknik seçiliyken */}
        {selectedTechnics.length > 0 && (
          <div className="technic-filter-bar">
            <div className="technic-filter-bar-modes">
              <span className="technic-filter-bar-label">Eşleşme</span>
              <div
                className="technic-match-modes"
                role="radiogroup"
                aria-label="Teknik eşleşme modu"
              >
                {TECHNIC_MATCH_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={technicMatchMode === mode.value}
                    className={`technic-match-mode ${
                      technicMatchMode === mode.value ? "is-active" : ""
                    }`}
                    onClick={() => setTechnicMatchMode(mode.value)}
                    title={mode.hint}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <span className="technic-match-hint">
                {getTechnicMatchHint(technicMatchMode)}
              </span>
            </div>

            <div className="technic-filter-chips">
              {selectedTechnics.map((tech) => (
                <span key={tech.technicId} className="technic-filter-chip">
                  {tech.name}
                  <button
                    type="button"
                    onClick={() => handleTechnicSelect(tech)}
                    aria-label={`${tech.name} filtreden kaldır`}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="11"
                      height="11"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="technic-filter-chips-clear"
                onClick={handleClearTechnics}
              >
                Tümünü temizle
              </button>
            </div>
          </div>
        )}
      </div>

      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleTechnicSelect}
        selectedTechnics={selectedTechnics}
        onClearAll={handleClearTechnics}
        workshops={workshops}
      />

      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <FilterTh label="Order ID" colKey="orderId" ctx={orderCf} />
              <SortFilterTh label="Firma" sortKey="firm" colKey="firm" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Model" sortKey="model" colKey="model" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Miktar" sortKey="quantity" colKey="quantity" type="float" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <FilterTh label="Birim" colKey="unit" ctx={orderCf} />
              <FilterTh label="Parça" colKey="pieceCount" type="int" ctx={orderCf} align="left" />
              <th>OK ?</th>
              <FilterTh label="Teknikler" colKey="technics" ctx={orderCf} />
              {user?.role === "Manager" && (
                <>
                  <SortFilterTh label="Birim Fiyat" sortKey="price" colKey="price" type="decimal" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
                  <FilterTh label="Döviz" colKey="currency" ctx={orderCf} />
                </>
              )}
              <SortFilterTh label="Atölye" sortKey="workshop" colKey="workshop" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Desinatör" sortKey="modelist" colKey="modelist" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Durum" sortKey="status" colKey="status" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Kabul Tarihi" sortKey="acceptanceDate" colKey="acceptanceDate" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Termin" sortKey="deadline" colKey="deadline" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <SortFilterTh label="Bitiş Tarihi" sortKey="completionDate" colKey="completionDate" type="datetime" ctx={orderCf} sortConfig={sortConfig} onSort={handleSort} />
              <FilterTh label="Not" colKey="note" ctx={orderCf} />
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {displayOrders.length === 0 ? (
              <tr>
                <td colSpan={16} style={{ textAlign: "center" }}>
                  {searchTerm.trim()
                    ? "Arama kriterine uygun sipariş bulunamadı."
                    : "Henüz sipariş bulunmuyor."}
                </td>
              </tr>
            ) : (
              displayOrders.map((order, index, array) => (
                <tr
                  key={order.orderId}
                  className={
                    openDropdownId === order.orderId ? "dropdown-open" : ""
                  }
                  data-row-index={index}
                >
                  <td
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(order.orderId);
                        setAlertModal({
                          isOpen: true,
                          message: `Order ID kopyalandı: ${order.orderId}`,
                          type: "success",
                        });
                      } catch (err) {
                        const textArea = document.createElement("textarea");
                        textArea.value = order.orderId;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          setAlertModal({
                            isOpen: true,
                            message: `Order ID kopyalandı: ${order.orderId}`,
                            type: "success",
                          });
                        } catch (e) {
                          setAlertModal({
                            isOpen: true,
                            message: "Kopyalama başarısız oldu.",
                            type: "error",
                          });
                        } finally {
                          document.body.removeChild(textArea);
                        }
                      }
                    }}
                    title="Kopyalamak için tıklayın"
                  >
                    {order.orderId.substring(0, 8)}...
                  </td>
                  <td title={order.firm?.firmName || "-"}>
                    {order.firm?.firmName
                      ? order.firm.firmName.length > 10
                        ? order.firm.firmName.substring(0, 10) + "..."
                        : order.firm.firmName
                      : "-"}
                  </td>
                  <td>{order.model?.modelName || "-"}</td>
                  <td>{order.quantity || 0}</td>
                  <td>
                    <span className="">{getUnitText(order.unit)}</span>
                  </td>
                  <td>
                    {order.unit === OrderUnit.Takim && order.pieceCount
                      ? `${order.pieceCount} parça`
                      : "-"}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={order.isSample || false}
                      onChange={() => handleToggleSample(order)}
                      disabled={loading}
                      style={{
                        cursor: loading ? "not-allowed" : "pointer",
                        width: "18px",
                        height: "18px",
                      }}
                      title={
                        order.isSample ? "Numune siparişi" : "Normal sipariş"
                      }
                    />
                  </td>
                  <td>
                    {order.orderTechnics && order.orderTechnics.length > 0 ? (
                      order.orderTechnics.length <= 3 ? (
                        <div className="technics-list">
                          {order.orderTechnics.map((ot, i) => (
                            <span key={i} className="technic-tag">
                              {ot.technic?.name || "-"}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="technics-expandable">
                          {expandedTechnics.has(order.orderId) ? (
                            <>
                              <div className="technics-list">
                                {order.orderTechnics.map((ot, i) => (
                                  <span key={i} className="technic-tag">
                                    {ot.technic?.name || "-"}
                                  </span>
                                ))}
                              </div>
                              <button
                                className="technics-toggle"
                                onClick={() => {
                                  const newSet = new Set(expandedTechnics);
                                  newSet.delete(order.orderId);
                                  setExpandedTechnics(newSet);
                                }}
                              >
                                Gizle ▲
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="technics-list">
                                {order.orderTechnics
                                  .slice(0, 1)
                                  .map((ot, i) => (
                                    <span key={i} className="technic-tag">
                                      {ot.technic?.name || "-"}
                                    </span>
                                  ))}
                              </div>
                              <button
                                className="technics-toggle"
                                onClick={() => {
                                  const newSet = new Set(expandedTechnics);
                                  newSet.add(order.orderId);
                                  setExpandedTechnics(newSet);
                                }}
                              >
                                +{order.orderTechnics.length - 2} daha ▼
                              </button>
                            </>
                          )}
                        </div>
                      )
                    ) : (
                      <span className="no-technic">-</span>
                    )}
                  </td>
                  {user?.role === "Manager" && (
                    <>
                      <td>{order.price ? formatNumber(order.price) : "-"}</td>
                      <td>
                        <span className="">
                          {order.priceCurrency || order.currency || "TRY"}
                        </span>
                      </td>
                    </>
                  )}
                  <td>{order.workshop?.name || "-"}</td>
                  <td>
                    {order.modelistUser
                      ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}`
                      : "-"}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${getStatusClass(order.status)}`}
                    >
                      {getStatusText(order.status)}
                    </span>
                  </td>
                  <td>{formatDate(order.acceptanceDate)}</td>
                  <td>{formatDate(order.deadline)}</td>
                  <td>
                    {order.completionDate
                      ? formatDate(order.completionDate)
                      : order.status === OrderStatus.Tamamlandi
                        ? "Tarih yok"
                        : "-"}
                  </td>
                  <td>
                    {order.note && order.note.trim() ? (
                      <div
                        className="note-badge"
                        onClick={() =>
                          setNoteModal({
                            isOpen: true,
                            note: order.note || "",
                            orderCode: order.model?.modelCode || "",
                            modelName: order.model?.modelName || "",
                          })
                        }
                        title="Notu görüntülemek için tıklayın"
                      >
                        <svg
                          className="note-badge-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                        <span className="note-badge-text">
                          {order.note.length > 15
                            ? order.note.substring(0, 15) + "..."
                            : order.note}
                        </span>
                        <svg
                          className="note-badge-expand"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                      </div>
                    ) : (
                      <span style={{ color: "#999" }}>-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-dropdown-container">
                      <button
                        className="action-dropdown-toggle"
                        onClick={(e) => {
                          e.stopPropagation();
                          const isOpening = openDropdownId !== order.orderId;
                          if (isOpening) {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const menuHeight = 250; // Approximate menu height
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const shouldOpenUp = spaceBelow < menuHeight;

                            setDropdownPosition({
                              top: shouldOpenUp
                                ? rect.top - menuHeight
                                : rect.bottom + 4,
                              left: rect.right - 200, // 200 is menu width
                            });
                            setOpenDropdownId(order.orderId);
                          } else {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                          }
                        }}
                        title="Aksiyonlar"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>

                      {openDropdownId === order.orderId && dropdownPosition && (
                        <div
                          className="action-dropdown-menu"
                          style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                          }}
                        >
                          {(order.qrCodeUrl || order.qrCode) && (
                            <button
                              className="action-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(null);
                                setDropdownPosition(null);
                                handleShowQrModal(order);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                              </svg>
                              <span>QR Detayı</span>
                            </button>
                          )}
                          {user?.role === "Manager" && (
                            <button
                              className="action-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(null);
                                setDropdownPosition(null);
                                setDetailModal({
                                  isOpen: true,
                                  orderId: order.orderId,
                                });
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              <span>Detay</span>
                            </button>
                          )}
                          {user && (
                            <a
                              href={`/orderdetail/${order.orderId}`}
                              className="action-dropdown-item"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="9 18 15 12 9 6"></polyline>
                              </svg>
                              <span>Transfer</span>
                            </a>
                          )}

                          {user?.role === "Manager" && order.modelId && (
                            <button
                              className="action-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(null);
                                setDropdownPosition(null);
                                setOrderCostsModal({
                                  isOpen: true,
                                  orderId: order.orderId,
                                  modelId: order.modelId!,
                                });
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="12" y1="1" x2="12" y2="23"></line>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                              </svg>
                              <span>Sipariş Maliyetleri</span>
                            </button>
                          )}

                          <button
                            className="action-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              setDropdownPosition(null);
                              setEditModal({
                                isOpen: true,
                                orderId: order.orderId,
                              });
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            <span>Düzenle</span>
                          </button>

                          {user?.role === "Manager" && (
                            <button
                              className="action-dropdown-item action-dropdown-item-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(null);
                                setDropdownPosition(null);
                                handleDeleteOrder(order);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                              <span>Sil</span>
                            </button>
                          )}
                          <button
                            className="action-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              setDropdownPosition(null);
                              setSelectedOrderForImages(order.orderId);
                              setShowImageManager(true);
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                x="3"
                                y="3"
                                width="18"
                                height="18"
                                rx="2"
                                ry="2"
                              ></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                            <span>Resimleri Görüntüle</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Card görünümü - mobil için */}
      <div className="orders-cards">
        {displayOrders.length === 0 ? (
          <div className="order-card">
            <div className="order-card-header">
              <div className="order-card-title">
                {searchTerm.trim()
                  ? "Arama kriterine uygun sipariş bulunamadı"
                  : "Sipariş bulunamadı"}
              </div>
            </div>
          </div>
        ) : (
          displayOrders.map((order) => (
            <div key={order.orderId} className="order-card">
              <div className="order-card-header">
                <div
                  className="order-card-title"
                  title={order.firm?.firmName || "Firma bilinmiyor"}
                >
                  {order.firm?.firmName
                    ? order.firm.firmName.length > 10
                      ? order.firm.firmName.substring(0, 10) + "..."
                      : order.firm.firmName
                    : "Firma bilinmiyor"}
                </div>
                <span
                  className={`status-badge order-card-status ${getStatusClass(
                    order.status,
                  )}`}
                >
                  {getStatusText(order.status)}
                </span>
              </div>

              <div className="order-card-info">
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Order ID</div>
                  <div
                    className="order-card-info-value"
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(order.orderId);
                        setAlertModal({
                          isOpen: true,
                          message: `Order ID kopyalandı: ${order.orderId}`,
                          type: "success",
                        });
                      } catch (err) {
                        const textArea = document.createElement("textarea");
                        textArea.value = order.orderId;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          setAlertModal({
                            isOpen: true,
                            message: `Order ID kopyalandı: ${order.orderId}`,
                            type: "success",
                          });
                        } catch (e) {
                          setAlertModal({
                            isOpen: true,
                            message: "Kopyalama başarısız oldu.",
                            type: "error",
                          });
                        } finally {
                          document.body.removeChild(textArea);
                        }
                      }
                    }}
                    title="Kopyalamak için tıklayın"
                  >
                    {order.orderId.substring(0, 8)}...
                  </div>
                </div>
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Model</div>
                  <div className="order-card-info-value">
                    {order.model?.modelName || "Model bilinmiyor"}
                  </div>
                </div>
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Miktar</div>
                  <div className="order-card-info-value">
                    {order.quantity} {getUnitText(order.unit)}
                  </div>
                </div>
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Kabul Tarihi</div>
                  <div className="order-card-info-value">
                    {formatDate(order.acceptanceDate)}
                  </div>
                </div>
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Termin</div>
                  <div className="order-card-info-value">
                    {formatDate(order.deadline)}
                  </div>
                </div>
                <div className="order-card-info-item">
                  <div className="order-card-info-label">Numune</div>
                  <div className="order-card-info-value">
                    <input
                      type="checkbox"
                      checked={order.isSample || false}
                      onChange={() => handleToggleSample(order)}
                      disabled={loading}
                      style={{
                        cursor: loading ? "not-allowed" : "pointer",
                        width: "18px",
                        height: "18px",
                      }}
                      title={
                        order.isSample ? "Numune siparişi" : "Normal sipariş"
                      }
                    />
                  </div>
                </div>
                {order.modelistUser && (
                  <div className="order-card-info-item">
                    <div className="order-card-info-label">Desinatör</div>
                    <div className="order-card-info-value">
                      {order.modelistUser.firstName}{" "}
                      {order.modelistUser.lastName}
                    </div>
                  </div>
                )}
                {order.note && order.note.trim() && (
                  <div className="order-card-info-item">
                    <div className="order-card-info-label">Not</div>
                    <div className="order-card-info-value">
                      <div
                        className="note-badge"
                        onClick={() =>
                          setNoteModal({
                            isOpen: true,
                            note: order.note || "",
                            orderCode: order.model?.modelCode || "",
                            modelName: order.model?.modelName || "",
                          })
                        }
                        title="Notu görüntülemek için tıklayın"
                      >
                        <svg
                          className="note-badge-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                        <span className="note-badge-text">
                          {order.note.length > 12
                            ? order.note.substring(0, 12) + "..."
                            : order.note}
                        </span>
                        <svg
                          className="note-badge-expand"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="order-card-actions">
                {(order.qrCodeUrl || order.qrCode) && (
                  <button
                    onClick={() => handleShowQrModal(order)}
                    className="btn btn-sm btn-secondary"
                    title="QR Kodu"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    QR
                  </button>
                )}
                {user && (
                  <button
                    onClick={() => navigate(`/orderdetail/${order.orderId}`)}
                    className="btn btn-icon btn-sm btn-secondary"
                    title="Transfer"
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                )}
                {user?.role === "Manager" && order.modelId && (
                  <button
                    onClick={() =>
                      setOrderCostsModal({
                        isOpen: true,
                        orderId: order.orderId,
                        modelId: order.modelId!,
                      })
                    }
                    className="btn btn-icon btn-sm btn-soft"
                    title="Sipariş Maliyetleri"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="1" x2="12" y2="23"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  </button>
                )}
                {(user?.role === "Manager" || user?.role === "Sekreterya") && (
                  <button
                    onClick={() =>
                      setEditModal({ isOpen: true, orderId: order.orderId })
                    }
                    className="btn btn-icon btn-sm btn-secondary"
                    title="Düzenle"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                )}
                {user?.role === "Manager" && (
                  <button
                    onClick={() => handleDeleteOrder(order)}
                    className="btn btn-icon btn-sm btn-soft-danger"
                    title="Sil"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedOrderForImages(order.orderId);
                    setShowImageManager(true);
                  }}
                  className="btn btn-sm btn-secondary"
                  title="Resimler"
                >
                  Resimler
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}

      {/* QR Print Modal */}
      {qrPrintModal.show && qrPrintModal.order && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "15px",
                borderRadius: "12px",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: "0 0 8px 0", fontSize: "24px" }}>
                ✅ Sipariş QR Detayı
              </h2>
              <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
                Sipariş No: <strong>{qrPrintModal.order.orderId}</strong>
              </p>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <p
                style={{
                  fontSize: "16px",
                  color: "#333",
                  marginBottom: "15px",
                  fontWeight: "600",
                }}
              >
                📋 Sipariş Bilgileri
              </p>
              <div
                style={{
                  textAlign: "left",
                  background: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <p style={{ margin: "6px 0" }}>
                  <strong>Firma:</strong>{" "}
                  {qrPrintModal.order.firm?.firmName || "-"}
                </p>
                <p style={{ margin: "6px 0" }}>
                  <strong>Model:</strong>{" "}
                  {qrPrintModal.order.model?.modelName || "-"}
                </p>
                <p style={{ margin: "6px 0" }}>
                  <strong>Miktar:</strong> {qrPrintModal.order.quantity}
                </p>
                <p style={{ margin: "6px 0" }}>
                  <strong>Atölye:</strong>{" "}
                  {qrPrintModal.order.workshop?.name || "-"}
                </p>
                {qrPrintModal.order.note && (
                  <p style={{ margin: "6px 0" }}>
                    <strong>Not:</strong> {qrPrintModal.order.note}
                  </p>
                )}
              </div>
            </div>

            {qrPrintModal.qrCodeDataUrl ? (
              <>
                <div
                  style={{
                    background: "#f0f4ff",
                    padding: "15px",
                    borderRadius: "12px",
                    marginBottom: "20px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#667eea",
                      marginBottom: "10px",
                      fontWeight: "600",
                    }}
                  >
                    📱 QR Kod Hazır
                  </p>
                  <div
                    style={{
                      background: "white",
                      padding: "15px",
                      borderRadius: "8px",
                      display: "inline-block",
                    }}
                  >
                    <img
                      src={qrPrintModal.qrCodeDataUrl}
                      alt="Order QR Code"
                      style={{
                        width: "200px",
                        height: "200px",
                        display: "block",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => {
                      const printWindow = window.open("", "_blank");
                      if (
                        printWindow &&
                        qrPrintModal.qrCodeDataUrl &&
                        qrPrintModal.order
                      ) {
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>QR Kod - ${
                                qrPrintModal.order.orderId
                              }</title>
                              <style>
                                body {
                                  display: flex;
                                  flex-direction: column;
                                  align-items: center;
                                  justify-content: center;
                                  min-height: 100vh;
                                  margin: 0;
                                  font-family: Arial, sans-serif;
                                }
                                .qr-container {
                                  text-align: center;
                                  padding: 20px;
                                }
                                img {
                                  width: 350px;
                                  height: 350px;
                                  margin: 20px 0;
                                }
                                .info {
                                  margin: 10px 0;
                                  font-size: 30px;
                                }
                                h2 {
                                  font-size: 30px;
                                }
                                @media print {
                                  body { margin: 0; }
                                }
                              </style>
                            </head>
                            <body>
                              <div class="qr-container">
                                <h2>Sipariş QR Kodu</h2>
                                <img src="${
                                  qrPrintModal.qrCodeDataUrl
                                }" alt="QR Code" />
                                <div class="info"><strong>Sipariş No:</strong> ${
                                  qrPrintModal.order.orderId
                                }</div>
                                <div class="info"><strong>Firma:</strong> ${
                                  qrPrintModal.order.firm?.firmName || "-"
                                }</div>
                                <div class="info"><strong>Model:</strong> ${
                                  qrPrintModal.order.model?.modelName || "-"
                                }</div>
                                <div class="info"><strong>Miktar:</strong> ${
                                  qrPrintModal.order.quantity
                                }</div>
                                ${
                                  qrPrintModal.order.note
                                    ? `<div class="info"><strong>Not:</strong> ${qrPrintModal.order.note}</div>`
                                    : ""
                                }
                              </div>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        setTimeout(() => {
                          printWindow.print();
                        }, 250);
                      }
                    }}
                    style={{
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: "600",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    🖨️ QR Kodu Yazdır
                  </button>

                  <a
                    href={qrPrintModal.qrCodeDataUrl || ""}
                    download={`order-${qrPrintModal.order.orderId}-qr.png`}
                    style={{
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: "600",
                      background: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      textDecoration: "none",
                      cursor: "pointer",
                      display: "inline-block",
                      transition: "transform 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    📥 QR Kodu İndir
                  </a>
                </div>
              </>
            ) : (
              <div
                style={{
                  background: "#fff3cd",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "15px",
                  color: "#856404",
                  fontSize: "14px",
                }}
              >
                <p>⚠️ QR kod oluşturulamadı.</p>
              </div>
            )}

            <button
              onClick={() =>
                setQrPrintModal({
                  show: false,
                  order: null,
                  qrCodeDataUrl: null,
                })
              }
              style={{
                marginTop: "15px",
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: "500",
                background: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={detailModal.isOpen}
        orderId={detailModal.orderId}
        onClose={() => setDetailModal({ isOpen: false, orderId: "" })}
      />

      {/* Order Edit Modal */}
      <OrderEditModal
        isOpen={editModal.isOpen}
        orderId={editModal.orderId}
        onClose={() => setEditModal({ isOpen: false, orderId: "" })}
        onSave={() => {
          setEditModal({ isOpen: false, orderId: "" });
          loadOrders();
        }}
      />

      {/* Order Costs Modal */}
      <OrderCostsModal
        isOpen={orderCostsModal.isOpen}
        orderId={orderCostsModal.orderId}
        modelId={orderCostsModal.modelId}
        onClose={() =>
          setOrderCostsModal({ isOpen: false, orderId: "", modelId: "" })
        }
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type="danger"
        confirmText="Sil"
        cancelText="İptal"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Note Modal */}
      <NoteModal
        isOpen={noteModal.isOpen}
        note={noteModal.note}
        orderCode={noteModal.orderCode}
        modelName={noteModal.modelName}
        onClose={() =>
          setNoteModal({
            isOpen: false,
            note: "",
            orderCode: "",
            modelName: "",
          })
        }
      />
    </div>
  );
};

export default Orders;
